import React from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Button, Card, SectionBand, Badge, FAQ, Input, Textarea,
  MotifBackground, LeakyBucket,
  TimeLeakIcon, MoneyLeakIcon, LostWorkLeakIcon, PricingLeakIcon, BlindSpotLeakIcon,
} from '@/kindra';
import { CheckCircle2, Mail, MapPin, Download, ArrowRight, ChevronRight, ShieldCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============================================================================
// /about — what Kindra does, why we exist, who we work with, how we work
// ============================================================================
export const AboutPage = () => (
  <>
    <section className="relative bg-navy text-icy overflow-hidden pt-28 pb-16" data-testid="about-hero">
      <MotifBackground surface="hero" />
      <div className="k-container relative max-w-3xl">
        <p className="k-eyebrow text-icy-muted mb-3">About Kindra</p>
        <h1 className="k-display text-icy">Run by people who&rsquo;ve <span className="k-spectrum-text">actually run businesses</span>.</h1>
        <p className="k-body-l text-icy-muted mt-5 max-w-prose">
          A small Aussie team that finds the holes in your bucket, plugs them quietly, and tells you when your bucket&rsquo;s actually fine.
        </p>
      </div>
    </section>

    {/* What Kindra does */}
    <SectionBand tone="white">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
        <div className="md:col-span-7 max-w-prose">
          <p className="k-eyebrow text-[var(--muted)] mb-3">What we do</p>
          <h2 className="k-h2 text-[var(--ink)]">Find your leaks. Plug them. Keep them sealed.</h2>
          <p className="k-body-l text-[var(--ink)] mt-4">
            Three layers. One model. Designed for Aussie SMBs that are flat out and don&rsquo;t know where the money&rsquo;s going.
          </p>
          <div className="mt-6 space-y-5" data-testid="about-three-steps">
            {[
              { n: 1, color: '#EB258D', h: 'The Leak Check', b: 'A free 30-min call, then the $1,950 Blueprint deep-dive. Five leak categories scored 0&ndash;100. Dollar values attached. 90-day action plan delivered in 2 weeks. No lock-in.' },
              { n: 2, color: '#9156C4', h: 'Implementation', b: 'We plug the leaks the Blueprint found. Fixed-price, done for you, paid 50/50.' },
              { n: 3, color: '#4F8FD0', h: 'Clarity Partner', b: 'A monthly rhythm so the bucket stays sealed. Plain-English P&amp;L review + the three things to do next.' },
            ].map((s) => (
              <div key={s.n} className="flex gap-4 items-start" data-testid={`about-step-${s.n}`}>
                <span
                  className="h-10 w-10 shrink-0 rounded-pill inline-flex items-center justify-center font-display font-bold text-icy text-[18px] leading-none"
                  style={{ background: s.color }}
                  aria-hidden
                >
                  {s.n}
                </span>
                <div className="min-w-0">
                  <p className="k-h3 text-[var(--ink)]">{s.h}</p>
                  <p className="k-body text-[var(--muted)] mt-1" dangerouslySetInnerHTML={{ __html: s.b }} />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="md:col-span-5">
          <Card className="p-6">
            <p className="k-eyebrow text-[var(--muted)] mb-3">Who we work with</p>
            <ul className="space-y-3 k-body text-[var(--ink)]">
              <li className="flex items-start gap-2"><CheckCircle2 size={16} className="text-sev-low mt-1 shrink-0" />Aussie SMBs &middot; $250k&ndash;$5M turnover</li>
              <li className="flex items-start gap-2"><CheckCircle2 size={16} className="text-sev-low mt-1 shrink-0" />Tradies &middot; owner-operators &middot; service teams</li>
              <li className="flex items-start gap-2"><CheckCircle2 size={16} className="text-sev-low mt-1 shrink-0" />Flat out, somehow not making money</li>
              <li className="flex items-start gap-2"><CheckCircle2 size={16} className="text-sev-low mt-1 shrink-0" />Nights doing admin instead of the actual work</li>
              <li className="flex items-start gap-2"><CheckCircle2 size={16} className="text-sev-low mt-1 shrink-0" />Want plain English, fixed prices, no lock-in</li>
            </ul>
          </Card>
        </div>
      </div>
    </SectionBand>

    {/* The founder — Michael's story */}
    <SectionBand tone="tint">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
        <div className="md:col-span-5">
          <img src="/brand/michael-founder.jpg" alt="Michael, founder of Kindra" loading="lazy" width="800" height="900" className="w-full rounded-lg shadow-e2" style={{ aspectRatio: '8 / 9' }} />
        </div>
        <div className="md:col-span-7 max-w-prose">
          <p className="k-eyebrow text-[var(--muted)] mb-3">Why we exist</p>
          <h2 className="k-h2 text-[var(--ink)]">I built Kindra because I was the owner being drained by it.</h2>
          <p className="k-body-l text-[var(--muted)] mt-4">
            Hi, I&rsquo;m Michael. I&rsquo;ve run real businesses &mdash; service teams, ops, the lot &mdash; and had every one of the five leaks myself. The Time Leak got me first: nights doing invoices instead of the actual work. Then the Pricing Leak &mdash; flat out and somehow not making money.
          </p>
          <p className="k-body text-[var(--muted)] mt-3">
            Most consultants point at the leak and leave. I wanted the opposite: a small team that finds the holes, plugs them quietly, and tells you when your bucket&rsquo;s actually fine. That&rsquo;s Kindra.
          </p>
        </div>
      </div>
    </SectionBand>

    {/* How we work */}
    <SectionBand tone="white">
      <div className="max-w-3xl">
        <p className="k-eyebrow text-[var(--muted)] mb-3">How we work</p>
        <h2 className="k-h2 text-[var(--ink)]">Plain English. Fixed prices. No lock-in.</h2>
      </div>
      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="about-how">
        {[
          { h: 'Plain English', b: 'No jargon, no acronyms, no consulting deck-speak. If your nan can&rsquo;t follow it, we rewrite it.' },
          { h: 'Fixed prices', b: 'Quoted up front in writing. No surprise invoices. No scope creep without sign-off.' },
          { h: 'No lock-in', b: 'Month-to-month on the Clarity Partner. Cancel anytime with 30 days&rsquo; notice. No long contracts.' },
          { h: 'Done for you', b: 'We do the work, not just point at it. Implementation is hands-on, not a slide deck.' },
          { h: 'Australian-owned', b: 'Brisbane HQ. Working with SMBs Australia-wide. Real human, real phone, real quick.' },
          { h: 'Guarantee', b: 'If the Blueprint doesn&rsquo;t surface three fixes worth more than $1,950 in conservative annual value, we refund the full fee.' },
        ].map((it, i) => (
          <Card key={i} className="p-6">
            <p className="k-h3 text-[var(--ink)]" dangerouslySetInnerHTML={{ __html: it.h }} />
            <p className="k-body text-[var(--muted)] mt-2" dangerouslySetInnerHTML={{ __html: it.b }} />
          </Card>
        ))}
      </div>
    </SectionBand>

    {/* CTA close — surface="cta" matches the footer motif intensity so the seam
        reads as truly continuous navy+motif with only the 2px spectrum
        hairline marking it. No second visual horizon. */}
    <section className="relative bg-navy text-icy overflow-hidden">
      <MotifBackground surface="cta" />
      <div className="k-container relative py-16 text-center max-w-2xl mx-auto">
        <h2 className="k-h2 text-icy">Want us to <span className="k-spectrum-text">find your leaks?</span></h2>
        <p className="k-body-l text-icy-muted mt-4">Start with the free 30-minute Leak Check call. No pitch, no jargon.</p>
        <div className="mt-7 flex justify-center gap-3 flex-wrap">
          <Button as={Link} to="/book" variant="primary" size="lg" data-testid="about-book-cta">Book a free 30-min call</Button>
          <Button as={Link} to="/contact" variant="secondary" size="lg" data-testid="about-contact-cta">Get in touch</Button>
        </div>
      </div>
    </section>
  </>
);

// ============================================================================
// /services — proper offer overview led by the Leak Check
// ============================================================================
export const ServicesPage = () => (
  <>
    <section className="relative bg-navy text-icy overflow-hidden pt-28 pb-14" data-testid="services-hero">
      <MotifBackground surface="hero" />
      <div className="k-container relative max-w-3xl">
        <p className="k-eyebrow text-icy-muted mb-3">Services</p>
        <h1 className="k-h1 text-icy">What Kindra does &mdash; <span className="k-spectrum-text">find your leaks, plug them, keep them sealed.</span></h1>
        <p className="k-body-l text-icy-muted mt-5 max-w-prose">Everything begins with a Leak Check. From there you pick what&rsquo;s worth fixing &mdash; and how much of it you want us to do.</p>
      </div>
    </section>

    {/* Section 1 — The Leak Check ($1,950) — the only thing you can buy today */}
    <SectionBand tone="white" id="leak-check" className="pt-12 md:pt-14">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
        <div className="md:col-span-7 max-w-prose">
          <div className="flex items-start gap-4">
            <span
              className="h-10 w-10 shrink-0 rounded-pill inline-flex items-center justify-center font-display font-bold text-icy text-[18px] leading-none"
              style={{ background: '#EB258D' }}
              aria-hidden
              data-testid="services-step-1"
            >
              1
            </span>
            <h2 className="k-h2 text-[var(--ink)] mt-0.5">The Blueprint.</h2>
          </div>
          <p className="k-body-l text-[var(--ink)] mt-4">A 2-week paid diagnostic. Five leak categories scored 0&ndash;100, every leak costed in dollars, a 90-day action plan you can run yourself.</p>
          <ul className="mt-5 space-y-2 k-body text-[var(--ink)]">
            <li className="flex items-start gap-2"><CheckCircle2 size={16} className="text-sev-low mt-1 shrink-0" />Free 30-min discovery call &mdash; book first, decide after</li>
            <li className="flex items-start gap-2"><CheckCircle2 size={16} className="text-sev-low mt-1 shrink-0" />Fixed price: <strong>$1,950 AUD</strong> &middot; paid up front &middot; no lock-in</li>
            <li className="flex items-start gap-2"><CheckCircle2 size={16} className="text-sev-low mt-1 shrink-0" />Delivered in 2 weeks: Scorecard PDF + Report PDF + Action Plan</li>
            <li className="flex items-start gap-2"><CheckCircle2 size={16} className="text-sev-low mt-1 shrink-0" />Credited 100% toward Implementation if you proceed</li>
            <li className="flex items-start gap-2"><ShieldCheck size={16} className="text-sev-low mt-1 shrink-0" /><strong>Guarantee:</strong> if the Report doesn&rsquo;t surface three fixes worth &gt;$1,950 in conservative annual value, we refund the full fee.</li>
          </ul>
          {/* Supporting line — positioning, not a duplicate price tag. Inter 15px navy-muted. */}
          <p className="mt-7 text-[15px] leading-relaxed text-[var(--muted)]" data-testid="services-positioning">
            A clear, dollar-figure picture of where your business is leaking. One short engagement. No lock-in.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Button as={Link} to="/book" variant="primary" size="lg" data-testid="services-book-cta">Book a free 30-min call</Button>
            <Button as={Link} to="/leak-check" variant="secondary" size="lg">How the Blueprint works <ChevronRight size={16} /></Button>
          </div>
        </div>
        <div className="md:col-span-5">
          <Card className="p-6 flex flex-col items-center" data-testid="services-leak-bucket">
            <div className="w-48"><LeakyBucket scores={{ time: 70, money: 60, lostwork: 75, pricing: 55, blindspot: 65 }} /></div>
            <p className="k-small text-[var(--muted)] mt-3 text-center max-w-[28ch]">Brightspark&rsquo;s bucket on day one: leaks scored 31 / 100 overall. Two weeks later: 67.</p>
          </Card>
        </div>
      </div>
    </SectionBand>

    {/* Sections 2/3/4 — Implementation, Clarity Partner, Fractional Ops.
        Identical numeral-pill treatment as section 1 (and About). Role-colour
        order maps each tier to the leak it primarily addresses:
          2 → Money  (Implementation moves the money)
          3 → Lost-Work (Clarity Partner = the recurring oversight that catches what slips)
          4 → Pricing (Fractional Ops = strategic operations)
        Subtle alignment with the 5-leak framework — no new design decisions. */}
    <SectionBand tone="tint" className="pt-12 md:pt-12">
      <h2 className="k-h2 text-[var(--ink)] mb-2">After the Blueprint, you pick what to fix.</h2>
      <p className="k-body-l text-[var(--muted)] max-w-prose mb-8">No upsell unless you want one. Most owners pick one of these three.</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="services-tiers">
        {[
          {
            n: 2,
            slug: 'implementation',
            color: '#9156C4',
            title: 'Implementation',
            price: 'From $4,000 &middot; fixed price &middot; 50/50',
            lede: 'We plug the leaks the Blueprint found. Done for you. Paid 50/50.',
            bullets: ['Starter $4k', 'Growth $9k', 'Scale $18k+'],
          },
          {
            n: 3,
            slug: 'clarity-partner',
            color: '#4F8FD0',
            title: 'Clarity Partner',
            price: 'From $1,500/mo &middot; no lock-in',
            lede: 'Monthly rhythm so the bucket stays sealed. Your numbers in English, every month.',
            bullets: ['Starter $1,500/mo', 'Growth $2,250/mo', 'Scale $2,950/mo'],
          },
          {
            n: 4,
            slug: 'fractional-ops',
            color: '#1FB6C1',
            title: 'Fractional Ops',
            price: 'From $2,950/mo &middot; no lock-in',
            lede: 'Senior ops thinking, part-time, without the headcount cost.',
            bullets: ['Weekly stand-up', 'Monthly review', 'On-call for the calls that matter'],
          },
        ].map((s) => (
          <Card key={s.slug} className="p-6 flex flex-col" data-testid={`services-tier-${s.slug}`}>
            <span
              className="h-10 w-10 shrink-0 rounded-pill inline-flex items-center justify-center font-display font-bold text-icy text-[18px] leading-none"
              style={{ background: s.color }}
              aria-hidden
              data-testid={`services-step-${s.n}`}
            >
              {s.n}
            </span>
            <h3 className="k-h3 text-[var(--ink)] mt-4">{s.title}</h3>
            <p className="k-small text-[var(--muted)] mt-1" dangerouslySetInnerHTML={{ __html: s.price }} />
            <p className="k-body text-[var(--ink)] mt-3" dangerouslySetInnerHTML={{ __html: s.lede }} />
            <ul className="mt-3 space-y-1 k-small text-[var(--muted)] flex-1">
              {s.bullets.map((b, i) => <li key={i} className="flex items-start gap-2"><span className="h-1.5 w-1.5 rounded-full bg-[var(--muted)] mt-2 shrink-0" />{b}</li>)}
            </ul>
            <Button as={Link} to={`/${s.slug}`} variant="secondary" className="mt-5 self-start">Read more <ChevronRight size={16} /></Button>
          </Card>
        ))}
      </div>
    </SectionBand>

    {/* CTA close — services. surface="cta" matches footer for continuous seam. */}
    <section className="relative bg-navy text-icy overflow-hidden">
      <MotifBackground surface="cta" />
      <div className="k-container relative py-16 text-center max-w-2xl mx-auto">
        <h2 className="k-h2 text-icy">Start where everyone starts: <span className="k-spectrum-text">the free 30-min call.</span></h2>
        <p className="k-body-l text-icy-muted mt-4">No pitch, no jargon &mdash; you&rsquo;ll leave knowing your biggest leak and what it&rsquo;s costing you.</p>
        <Button as={Link} to="/book" variant="primary" size="lg" className="mt-7" data-testid="services-final-cta">Book a free 30-min call</Button>
      </div>
    </section>
  </>
);

// ============================================================================
// /implementation /clarity-partner /fractional-ops — proper detail pages
// ============================================================================
const SERVICE_DETAILS = {
  'implementation': {
    title: 'Implementation',
    pricePill: 'From $4,000 &middot; fixed price &middot; 50/50',
    hero: "We plug the leaks the Blueprint found. Done for you, fixed price, no lock-in.",
    whatIs: "Implementation is what happens after the Blueprint. We take the highest-value leak in the Report and we plug it ourselves &mdash; workflow rebuilds, invoicing flows, follow-up engines, pricing reviews, the lot. You stay in the seat doing the actual work; we do the plumbing.",
    whoFor: [
      "Owners who don&rsquo;t have time to fix it themselves",
      "Businesses with a clear Blueprint scoring 40&ndash;60 (room to move, not on fire)",
      "Anyone who&rsquo;s sick of being told what&rsquo;s wrong without anyone fixing it",
    ],
    included: [
      "<strong>Scope locked in writing</strong> &mdash; what we&rsquo;re fixing, what we&rsquo;re not, what success looks like",
      "<strong>Weekly check-ins</strong> &mdash; 30 mins, plain English, no slide decks",
      "<strong>Hands-on build</strong> &mdash; we&rsquo;re in your tools, not on Zoom theorising",
      "<strong>Handover doc</strong> &mdash; how to run what we built without us, written for a non-technical owner",
      "<strong>30-day post-launch support</strong> &mdash; we don&rsquo;t walk away on day one",
    ],
    pricing: [
      { tier: 'Starter', price: '$4,000', body: 'One leak, one build. 4&ndash;6 weeks. Best for a single high-value fix (e.g. invoicing rebuild, follow-up engine).' },
      { tier: 'Growth',  price: '$9,000', body: 'Two leaks, integrated build. 6&ndash;10 weeks. Best for a workflow + a measurement layer.' },
      { tier: 'Scale',   price: '$18,000+', body: 'Three+ leaks, system-level rebuild. 10+ weeks. Best for owners stepping back from the day-to-day.' },
    ],
    faqs: [
      { q: 'How does the 50/50 payment split work?', a: 'Half the agreed fixed price up front (deposit), the other half on completion when you sign off the handover doc. No drip-feed invoices in between.' },
      { q: 'What if scope changes mid-build?', a: 'We pause, write up the change, quote it, you approve in writing, we proceed. No surprise invoices. Ever.' },
      { q: 'Do I have to do a Blueprint first?', a: 'Yes. We don&rsquo;t implement against guesses &mdash; we implement against a costed Blueprint Report. The $1,950 is credited 100% to Implementation if you proceed.' },
      { q: 'Is there a guarantee on outcomes?', a: 'We guarantee what we agreed to build, delivered to the scope spec. We can&rsquo;t guarantee specific business outcomes (your turnover, conversion rates, etc.) &mdash; too many variables outside our control.' },
    ],
  },
  'clarity-partner': {
    title: 'Clarity Partner',
    pricePill: 'From $1,500/mo &middot; no lock-in &middot; cancel anytime',
    hero: "A monthly rhythm so the bucket stays sealed. Your numbers, in English, every month.",
    whatIs: "After the Blueprint finds your leaks and Implementation plugs them, Clarity Partner is how you keep them plugged. Once a month we sit down (Zoom or in-person), walk through your numbers in plain English, and agree the three things to do next. We catch leaks before they re-open.",
    whoFor: [
      "Owners who don&rsquo;t want a full-time ops manager but need ops thinking",
      "Anyone who looks at their accountant&rsquo;s P&amp;L and goes &ldquo;cool, but what do I do about it?&rdquo;",
      "Businesses past the Blueprint who want a steady hand on the tiller",
    ],
    included: [
      "<strong>Monthly 90-min review</strong> &mdash; P&amp;L walkthrough in plain English",
      "<strong>The three things to do next</strong> &mdash; written up, costed, prioritised",
      "<strong>Quarterly bucket re-score</strong> &mdash; same 5 leak categories, watch them improve",
      "<strong>Slack/email access between meetings</strong> &mdash; quick gut-check on the calls that matter",
      "<strong>Annual deep-dive</strong> &mdash; full re-Leak-Check at no extra cost",
    ],
    pricing: [
      { tier: 'Starter', price: '$1,500/mo', body: 'Monthly review + 3 priorities + email access. For owners running it themselves.' },
      { tier: 'Growth',  price: '$2,250/mo', body: 'Adds a mid-month check-in + Slack access. For owners with one or two people in the seat.' },
      { tier: 'Scale',   price: '$2,950/mo', body: 'Adds quarterly deep-dives + on-call. For owners stepping back, growing the team.' },
    ],
    faqs: [
      { q: 'How does no lock-in work?', a: 'Month-to-month. Cancel anytime with 30 days&rsquo; written notice. No auto-renewals beyond the month, no early-exit fees.' },
      { q: 'Do I need a Blueprint first?', a: 'Strongly recommended. The whole point of Clarity Partner is keeping plugged leaks plugged &mdash; if we haven&rsquo;t scored the leaks, we&rsquo;re flying blind.' },
      { q: 'Can I pause if I go on holiday?', a: 'Yes. Pause one month at a time, no penalty. Just let us know two weeks before the next review.' },
      { q: 'What happens at the annual deep-dive?', a: 'Full re-Leak-Check &mdash; same 5 categories, same scoring, same Report. You see the year&rsquo;s movement and we agree the next 12 months&rsquo; priorities.' },
    ],
  },
  'fractional-ops': {
    title: 'Fractional Ops',
    pricePill: 'From $2,950/mo &middot; no lock-in &middot; part-time',
    hero: "Senior ops thinking, part-time, without the headcount cost.",
    whatIs: "When the Blueprint finds you don&rsquo;t need a full ops manager but you need ops thinking. We embed one of our team in your business 1&ndash;2 days a week &mdash; running the standups, holding the team accountable, escalating the calls that matter to you.",
    whoFor: [
      "Owners stepping back from the day-to-day but not ready to hire a $180k Ops Manager",
      "Teams of 4&ndash;15 that need senior structure without the senior salary",
      "Businesses growing past the founder-runs-everything stage",
    ],
    included: [
      "<strong>Weekly team stand-up</strong> &mdash; we run it, you don&rsquo;t",
      "<strong>Monthly leadership review</strong> &mdash; with you, the numbers, the team",
      "<strong>On-call for the calls that matter</strong> &mdash; hiring decisions, big quotes, supplier issues",
      "<strong>Process documentation</strong> &mdash; whatever we&rsquo;re running gets written down so it can be handed over later",
      "<strong>Quarterly leak re-score</strong> &mdash; same as Clarity Partner, included",
    ],
    pricing: [
      { tier: 'Starter', price: '$2,950/mo', body: '1 day/week embedded. Best for teams of 4&ndash;8.' },
      { tier: 'Growth',  price: '$4,950/mo', body: '2 days/week. Adds hiring + supplier escalations. Best for teams of 8&ndash;15.' },
      { tier: 'Scale',   price: 'Custom', body: '3+ days/week. Effectively interim Ops Manager. Quoted bespoke.' },
    ],
    faqs: [
      { q: 'Is this a permanent hire?', a: 'No. Fractional means part-time, on a rolling monthly. If you eventually want a full-time Ops Manager, we&rsquo;ll help you hire one and hand over cleanly.' },
      { q: 'Who&rsquo;s actually in my business?', a: 'A Kindra ops lead, vetted and supervised by Michael. You always know who&rsquo;s in your business and you can request a different lead at any time.' },
      { q: 'What hours / how do I reach them?', a: 'On their embedded day they&rsquo;re yours for the day. Off-day they&rsquo;re reachable on Slack/email within 4 working hours for non-urgent stuff, same-day for urgent.' },
      { q: 'How does this differ from Clarity Partner?', a: 'Clarity Partner is monthly advice from outside. Fractional Ops is weekly execution from inside &mdash; we&rsquo;re running things, not advising on them.' },
    ],
  },
};

export const ServiceDetailPage = ({ slug }) => {
  const s = SERVICE_DETAILS[slug] || SERVICE_DETAILS['implementation'];
  return (
    <>
      <section className="relative bg-navy text-icy overflow-hidden pt-28 pb-14" data-testid={`detail-hero-${slug}`}>
        <MotifBackground surface="hero" />
        <div className="k-container relative max-w-3xl">
          <Badge tone="spectrum" className="mb-3" dangerouslySetInnerHTML={{ __html: s.pricePill }} />
          <h1 className="k-h1 text-icy">{s.title}.</h1>
          <p className="k-body-l text-icy-muted mt-5 max-w-prose">{s.hero}</p>
          <div className="mt-7 flex gap-3 flex-wrap">
            <Button as={Link} to="/book" variant="primary" size="lg" data-testid={`detail-book-cta-${slug}`}>Start with a free Leak Check</Button>
            <Button as={Link} to="/services" variant="secondary" size="lg">All services <ChevronRight size={16} /></Button>
          </div>
        </div>
      </section>

      <SectionBand tone="white">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-7 max-w-prose">
            <p className="k-eyebrow text-[var(--muted)] mb-3">What it is</p>
            <h2 className="k-h2 text-[var(--ink)]">Plain English. No deck-speak.</h2>
            <p className="k-body-l text-[var(--ink)] mt-4" dangerouslySetInnerHTML={{ __html: s.whatIs }} />
          </div>
          <aside className="md:col-span-5">
            <Card className="p-6">
              <p className="k-eyebrow text-[var(--muted)] mb-3">Who it&rsquo;s for</p>
              <ul className="space-y-2 k-body text-[var(--ink)]">
                {s.whoFor.map((b, i) => <li key={i} className="flex items-start gap-2"><CheckCircle2 size={16} className="text-sev-low mt-1 shrink-0" /><span dangerouslySetInnerHTML={{ __html: b }} /></li>)}
              </ul>
            </Card>
          </aside>
        </div>
      </SectionBand>

      <SectionBand tone="tint">
        <p className="k-eyebrow text-[var(--muted)] mb-3">What&rsquo;s included</p>
        <h2 className="k-h2 text-[var(--ink)] mb-6">Everything in writing, nothing on a handshake.</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl">
          {s.included.map((b, i) => (
            <Card key={i} className="p-5">
              <p className="k-body text-[var(--ink)]" dangerouslySetInnerHTML={{ __html: b }} />
            </Card>
          ))}
        </div>
      </SectionBand>

      <SectionBand tone="white" id="pricing">
        <p className="k-eyebrow text-[var(--muted)] mb-3">Pricing</p>
        <h2 className="k-h2 text-[var(--ink)] mb-6">Three tiers. Pick what fits.</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {s.pricing.map((p, i) => (
            <Card key={i} className="p-6 flex flex-col" data-testid={`detail-tier-${slug}-${p.tier.toLowerCase()}`}>
              <p className="k-eyebrow text-[var(--muted)]">{p.tier}</p>
              <p className="font-display font-bold text-[36px] k-spectrum-text mt-2">{p.price}</p>
              <p className="k-body text-[var(--muted)] mt-3 flex-1" dangerouslySetInnerHTML={{ __html: p.body }} />
              <Button as={Link} to="/book" variant="secondary" className="mt-5 self-start">Start with a free 30-min call</Button>
            </Card>
          ))}
        </div>
      </SectionBand>

      <SectionBand tone="tint">
        <p className="k-eyebrow text-[var(--muted)] mb-3">FAQs</p>
        <h2 className="k-h2 text-[var(--ink)] mb-6">Honest answers.</h2>
        <FAQ items={s.faqs} />
      </SectionBand>

      {/* CTA close — service detail. surface="cta" matches footer for continuous seam. */}
      <section className="relative bg-navy text-icy overflow-hidden">
        <MotifBackground surface="cta" />
        <div className="k-container relative py-16 text-center max-w-2xl mx-auto">
          <h2 className="k-h2 text-icy">Ready to start? <span className="k-spectrum-text">Start with the call.</span></h2>
          <p className="k-body-l text-icy-muted mt-4">Free 30 minutes. No pitch. You leave knowing your biggest leak and what it&rsquo;s costing you.</p>
          <Button as={Link} to="/book" variant="primary" size="lg" className="mt-7">Book a free 30-min call</Button>
        </div>
      </section>
    </>
  );
};

// ============================================================================
// /results — Brightspark Electrical only (locked illustrative case) + honest
// waiting-room. NEVER invent customer names, testimonials, or dollar figures
// attributed to specific businesses. Brightspark is the canonical illustrative
// example used across every Kindra artefact — keep it; flesh it out, don't
// duplicate it across made-up names.
// ============================================================================
// Brightspark stays as the canonical flagship illustrative — kept verbatim.
const BRIGHTSPARK_SCORES = { time: 24, money: 28, lostwork: 38, pricing: 32, blindspot: 33 }; // avg 31 — locked
const BRIGHTSPARK_AFTER  = { time: 70, money: 65, lostwork: 70, pricing: 60, blindspot: 70 }; // avg 67 — locked

const BRIGHTSPARK_LEAKS_FOUND = [
  { key: 'time',      name: 'Time Leak',      role: '#EB258D',
    finding: 'Owner doing 14 hours/week of admin instead of high-margin install work.',
    dollars: '~$28,000/yr' },
  { key: 'money',     name: 'Money Leak',     role: '#9156C4',
    finding: '$42,000 in invoices >30 days overdue, no follow-up system.',
    dollars: '~$8,000/yr in cashflow drag' },
  { key: 'lostwork',  name: 'Lost-Work Leak', role: '#4F8FD0',
    finding: '11 quotes per month going out slow, average 4 days.',
    dollars: '~$24,000/yr in won-but-late jobs' },
  { key: 'pricing',   name: 'Pricing Leak',   role: '#1FB6C1',
    finding: 'Call-out fee unchanged for 3 years, parts margin at 8% (industry standard 18%).',
    dollars: '~$31,000/yr in margin gap' },
  { key: 'blindspot', name: 'Blind-Spot Leak',role: '#34D1B0',
    finding: 'No monthly P&L review.',
    dollars: '~$10,000/yr in decisions made on gut' },
];

const BRIGHTSPARK_PLUGS = [
  'Sorted admin — VA + a job-management tool. Owner back to 4 hours of admin/week.',
  'Built an automated invoice follow-up sequence. Overdue invoices down to $12,000 within 60 days.',
  'Quote turnaround down from 4 days to same-day with a template system.',
  'Repriced — call-out fee up 35%, parts margin up to industry standard. Zero customer losses.',
  'Monthly P&L review in plain English, 15 minutes, with the owner.',
];

// 4 illustrative archetypes — labelled, no proper-noun business names, no
// fictional testimonial quotes. Numbers consistent with Brightspark distribution
// scale. Updated PRD code-of-conduct rule allows this; see /app/memory/PRD.md.
const ARCHETYPES = [
  {
    slug: 'sunshine-coast-cafe',
    label: 'Sunshine Coast café',
    region: 'Queensland',
    size: '12 people',
    before: 38,
    after: 64,
    beforeDist: { time: 36, money: 28, lostwork: 44, pricing: 50, blindspot: 32 },
    afterDist:  { time: 62, money: 70, lostwork: 65, pricing: 58, blindspot: 65 },
    headline: 'From counting cash at midnight to clean weekly numbers.',
    setup: 'A 12-person hospitality business doing solid covers but with no clear view on weekly margin. POS reconciled monthly (sometimes), the owner couldn\u2019t answer "did we make money last week?".',
    leaks: [
      { key: 'time',      name: 'Time Leak',      role: '#EB258D', finding: 'Owner spending 8 hours/week on supplier invoice matching by hand.', dollars: '~$11,000/yr' },
      { key: 'money',     name: 'Money Leak',     role: '#9156C4', finding: 'POS reconciliation gap &mdash; 3.2% of cash takings slipping through fortnightly.', dollars: '~$26,000/yr' },
      { key: 'lostwork',  name: 'Lost-Work Leak', role: '#4F8FD0', finding: 'Two regular catering enquiries/week not followed up properly.', dollars: '~$14,000/yr' },
      { key: 'pricing',   name: 'Pricing Leak',   role: '#1FB6C1', finding: 'Six menu items priced below cost-plus-margin target.', dollars: '~$18,000/yr' },
      { key: 'blindspot', name: 'Blind-Spot Leak',role: '#34D1B0', finding: 'No weekly P&L; monthly only, often two weeks late.',                            dollars: '~$9,000/yr' },
    ],
    plugs: [
      'Weekly P&L review with the owner &mdash; 20 minutes Monday morning.',
      'Repriced 6 menu items; net price lift 11% with zero measurable churn.',
      'Automated supplier invoice matching against deliveries (Xero rule + bank feed).',
      'Catering enquiry intake template + a 48-hour callback rule.',
      'POS daily Z-read reconciled to bank deposit by the duty manager, not the owner.',
    ],
    totalFound: '~$78,000/yr',
    outcome: 'Owner now knows the week\u2019s margin by Monday lunchtime. Sleeping again.',
  },
  {
    slug: 'brisbane-accounting',
    label: 'Brisbane mid-size accounting practice',
    region: 'Queensland',
    size: '18 people',
    before: 27,
    after: 62,
    beforeDist: { time: 18, money: 30, lostwork: 32, pricing: 30, blindspot: 25 },
    afterDist:  { time: 58, money: 65, lostwork: 62, pricing: 65, blindspot: 60 },
    headline: 'From partners doing admin to partners doing partner work.',
    setup: 'An 18-person professional services firm. Two senior partners doing client admin and timesheet chasing instead of advisory. No view on which clients were actually profitable per hour.',
    leaks: [
      { key: 'time',      name: 'Time Leak',      role: '#EB258D', finding: 'Partners doing 6&ndash;8 hours/week of admin each (billed out at $250+/hr).',  dollars: '~$48,000/yr' },
      { key: 'money',     name: 'Money Leak',     role: '#9156C4', finding: 'WIP &gt; 60 days at $140k. Old jobs never written off or invoiced.',           dollars: '~$22,000/yr' },
      { key: 'lostwork',  name: 'Lost-Work Leak', role: '#4F8FD0', finding: 'Cross-sell opportunities going stale &mdash; bookkeeping clients never offered SMSF or advisory.', dollars: '~$26,000/yr' },
      { key: 'pricing',   name: 'Pricing Leak',   role: '#1FB6C1', finding: 'Compliance work priced flat for 5 years; effective margin down to ~12%.',     dollars: '~$28,000/yr' },
      { key: 'blindspot', name: 'Blind-Spot Leak',role: '#34D1B0', finding: 'No client-level profitability view &mdash; some clients quietly losing money.', dollars: '~$8,000/yr' },
    ],
    plugs: [
      'VA + workflow tool &mdash; partners back to advisory work full-time.',
      'WIP triage: write off, invoice, or close. Got back to &lt; $40k WIP > 60 days.',
      'Cross-sell intake template at every quarterly review meeting.',
      'Compliance work repriced 18%. Pre-flagged with clients; net retention unchanged.',
      'Client-profitability dashboard refreshed weekly &mdash; top-10 + bottom-10 reviewed monthly.',
    ],
    totalFound: '~$132,000/yr',
    outcome: 'Partners back to senior work. Two clients quietly off-boarded, three repriced. Practice margin up.',
  },
  {
    slug: 'gold-coast-plumbing',
    label: 'Gold Coast plumbing business',
    region: 'Queensland',
    size: '4 people',
    before: 42,
    after: 71,
    beforeDist: { time: 50, money: 48, lostwork: 30, pricing: 28, blindspot: 54 },
    afterDist:  { time: 75, money: 72, lostwork: 68, pricing: 70, blindspot: 70 },
    headline: 'From quoting at night to weekends back.',
    setup: 'A 4-person plumbing operation &mdash; sole trader plus three on-tools. Quotes were going out slow, mate-rates were creeping in, and the owner was doing the books at 9pm.',
    leaks: [
      { key: 'time',      name: 'Time Leak',      role: '#EB258D', finding: 'Owner quoting and invoicing after-hours &mdash; ~9 hours/week.',                dollars: '~$13,000/yr' },
      { key: 'money',     name: 'Money Leak',     role: '#9156C4', finding: 'Deposits inconsistent. ~$24k tied up in jobs running over due dates.',         dollars: '~$6,000/yr in cashflow drag' },
      { key: 'lostwork',  name: 'Lost-Work Leak', role: '#4F8FD0', finding: 'Quotes taking 5&ndash;7 days. Industry benchmark is &lt; 48 hours.',           dollars: '~$32,000/yr in won-but-late jobs' },
      { key: 'pricing',   name: 'Pricing Leak',   role: '#1FB6C1', finding: 'Mate-rates on 1 in 4 jobs. Call-out fee 30% below market.',                    dollars: '~$38,000/yr' },
      { key: 'blindspot', name: 'Blind-Spot Leak',role: '#34D1B0', finding: 'No monthly numbers review &mdash; just the year-end tax meeting.',             dollars: '~$7,000/yr' },
    ],
    plugs: [
      'Same-day quote template + a "send before close-of-business" rule.',
      'Call-out fee up 40% (in line with current market). Pre-flagged to existing clients.',
      'Standard 30% deposit on any job > $1,500. Cashflow on the right side of the ledger.',
      '"No-mate-rates" policy with a clean exit script. Lost two jobs, kept margin on the rest.',
      'Monthly P&L review with the owner. 15 minutes. Plain English.',
    ],
    totalFound: '~$96,000/yr',
    outcome: 'Quotes go same-day. Mate-rates retired. Owner has Sundays back.',
  },
  {
    slug: 'toowoomba-landscaping',
    label: 'Toowoomba landscape business',
    region: 'Queensland',
    size: '5 people',
    before: 34,
    after: 66,
    beforeDist: { time: 26, money: 30, lostwork: 42, pricing: 38, blindspot: 34 },
    afterDist:  { time: 65, money: 68, lostwork: 65, pricing: 65, blindspot: 65 },
    headline: 'From quoting after hours to deposits paid before the truck arrives.',
    setup: 'A 5-person landscape and outdoor-construction business. Owner running the team during the day and quoting at 9pm. Half the jobs starting without a deposit. Cash always 30 days behind work delivered.',
    leaks: [
      { key: 'time',      name: 'Time Leak',      role: '#EB258D', finding: 'Owner doing all quotes after hours &mdash; ~11 hours/week.',                  dollars: '~$22,000/yr' },
      { key: 'money',     name: 'Money Leak',     role: '#9156C4', finding: 'Deposits collected on roughly half of jobs. Cashflow 30+ days behind work.', dollars: '~$18,000/yr in cashflow drag' },
      { key: 'lostwork',  name: 'Lost-Work Leak', role: '#4F8FD0', finding: 'Quote-to-invoice flow manual; ~9 jobs/month finishing without an invoice raised same-week.', dollars: '~$16,000/yr' },
      { key: 'pricing',   name: 'Pricing Leak',   role: '#1FB6C1', finding: 'Standard hourly rate hadn\u2019t moved in 4 years.',                          dollars: '~$22,000/yr' },
      { key: 'blindspot', name: 'Blind-Spot Leak',role: '#34D1B0', finding: 'No job-margin view &mdash; suspecting some big jobs were losing money. Confirmed.', dollars: '~$9,000/yr' },
    ],
    plugs: [
      'Part-time admin support (2 days/week) running quotes + invoicing.',
      'Standard 40% deposit policy on every job > $2,000. No exceptions.',
      'Quote-to-invoice automation &mdash; job complete → invoice raised same-day.',
      'Hourly rate up 14% in line with regional market. Pre-flagged to repeat clients.',
      'Per-job margin reporting at month-end &mdash; top-3 and bottom-3 reviewed.',
    ],
    totalFound: '~$87,000/yr',
    outcome: 'Quotes leave the office in business hours. Deposits in. Margin known per job.',
  },
];

const CaseStudyCard = ({ archetype: a }) => (
  <SectionBand tone="tint" id={a.slug} className="pt-12 md:pt-14">
    <p className="k-eyebrow text-[var(--muted)] mb-3">{a.label} &middot; {a.region} &middot; {a.size}</p>
    <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
      <div className="md:col-span-7 max-w-prose">
        <h2 className="k-h2 text-[var(--ink)]">{a.headline.endsWith('.') ? a.headline.slice(0, -1) : a.headline} <span className="k-spectrum-text">{a.headline.endsWith('.') ? '.' : '.'}</span></h2>
        <p className="k-body-l text-[var(--ink)] mt-4" dangerouslySetInnerHTML={{ __html: a.setup }} />
        <p className="k-eyebrow text-[var(--muted)] mt-8 mb-3">What we found (in plain English)</p>
        <ul className="space-y-3">
          {a.leaks.map((l) => (
            <li key={l.key} className="flex items-start gap-3 k-body text-[var(--ink)]">
              <span className="h-2 w-2 rounded-full mt-2 shrink-0" style={{ background: l.role }} aria-hidden />
              <span>
                <strong style={{ color: l.role }}>{l.name}</strong>
                <span className="text-[var(--ink)]"> &mdash; <span dangerouslySetInnerHTML={{ __html: l.finding }} /></span>
                <span className="block k-small text-[var(--muted)] mt-0.5">Cost: <strong className="text-[var(--ink)]">{l.dollars}</strong>.</span>
              </span>
            </li>
          ))}
        </ul>
        <p className="k-body-l text-[var(--ink)] mt-6"><strong>Total found:</strong> {a.totalFound} in plain-English leaks.</p>
        <p className="k-eyebrow text-[var(--muted)] mt-8 mb-3">What we plugged (over 90 days)</p>
        <ul className="space-y-2">
          {a.plugs.map((p, i) => (
            <li key={i} className="flex items-start gap-2 k-body text-[var(--ink)]">
              <CheckCircle2 size={16} className="text-sev-low mt-1 shrink-0" />
              <span dangerouslySetInnerHTML={{ __html: p }} />
            </li>
          ))}
        </ul>
        <p className="k-body-l text-[var(--ink)] mt-6">
          <strong>Leak Score: {a.before} &rarr; {a.after} in 90 days.</strong> {a.outcome}
        </p>
      </div>
      <aside className="md:col-span-5 min-w-0" data-testid={`archetype-bucket-card-${a.slug}`}>
        <Card className="p-5 overflow-hidden">
          <p className="k-eyebrow text-[var(--muted)] mb-4 text-center">Bucket &middot; Day 1 &rarr; Day 90</p>
          <div className="grid grid-cols-2 gap-3 items-end">
            <div className="text-center min-w-0">
              <div className="w-full max-w-[160px] mx-auto"><LeakyBucket scores={a.beforeDist} fluid /></div>
              <p className="font-display font-bold text-[28px] text-[var(--ink)] mt-2 leading-none">{a.before}</p>
              <p className="k-small text-[var(--muted)] mt-1">before</p>
            </div>
            <div className="text-center min-w-0">
              <div className="w-full max-w-[160px] mx-auto"><LeakyBucket scores={a.afterDist} fluid /></div>
              <p className="font-display font-bold text-[28px] k-spectrum-text mt-2 leading-none">{a.after}</p>
              <p className="k-small text-[var(--muted)] mt-1">after 90 days</p>
            </div>
          </div>
        </Card>
      </aside>
    </div>
  </SectionBand>
);

export const ResultsPage = () => (
  <>
    <section className="relative bg-navy text-icy overflow-hidden pt-28 pb-14" data-testid="results-hero">
      <MotifBackground surface="hero" />
      <div className="k-container relative max-w-3xl">
        <p className="k-eyebrow text-icy-muted mb-3">Results</p>
        <h1 className="k-h1 text-icy">Real businesses. Leaks plugged. <span className="k-spectrum-text">Plain English.</span></h1>
        <p className="k-body-l text-icy-muted mt-5 max-w-prose">
          One flagship worked example plus four archetype scenarios &mdash; the kinds of leaks we see in Aussie SMBs. Numbers are realistic to the Kindra scale; real beta case studies replace these as they complete.
        </p>
      </div>
    </section>

    {/* Page-level contextual frame — replaces per-card ILLUSTRATIVE pills.
        One honest line at the top; cards below lead with their headline. */}
    <SectionBand tone="white" className="!pb-0">
      <p
        className="text-center text-[16px] leading-relaxed text-[var(--muted)] max-w-2xl mx-auto"
        data-testid="results-contextual-frame"
      >
        Worked examples &mdash; based on real patterns we see in Aussie SMBs. Real client case studies land as our beta delivers.
      </p>
    </SectionBand>

    {/* Brightspark Electrical — flagship illustrative case study (verbatim copy) */}
    <SectionBand tone="white" id="brightspark-electrical" className="pt-12 md:pt-14">
      <p className="k-eyebrow text-[var(--muted)] mb-3">Brightspark Electrical &middot; Brisbane &middot; 6 people</p>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
        <div className="md:col-span-7 max-w-prose">
          <h2 className="k-h2 text-[var(--ink)]">From flat out and unsure to <span className="k-spectrum-text">clear and trading.</span></h2>
          <p className="k-body-l text-[var(--ink)] mt-4">
            Brightspark is a 6-person electrical contracting business. The owner was working 60-hour weeks, paying himself last, and not sure where the money was actually going. We ran a Blueprint.
          </p>

          <p className="k-eyebrow text-[var(--muted)] mt-8 mb-3">What we found (in plain English)</p>
          <ul className="space-y-3" data-testid="brightspark-leaks-found">
            {BRIGHTSPARK_LEAKS_FOUND.map((l) => (
              <li key={l.key} className="flex items-start gap-3 k-body text-[var(--ink)]">
                <span className="h-2 w-2 rounded-full mt-2 shrink-0" style={{ background: l.role }} aria-hidden />
                <span>
                  <strong style={{ color: l.role }}>{l.name}</strong>
                  <span className="text-[var(--ink)]"> &mdash; {l.finding}</span>
                  <span className="block k-small text-[var(--muted)] mt-0.5">Cost: <strong className="text-[var(--ink)]">{l.dollars}</strong>.</span>
                </span>
              </li>
            ))}
          </ul>

          <p className="k-body-l text-[var(--ink)] mt-6"><strong>Total found:</strong> ~$101,000/yr in plain-English leaks.</p>

          <p className="k-eyebrow text-[var(--muted)] mt-8 mb-3">What we plugged (over 90 days)</p>
          <ul className="space-y-2" data-testid="brightspark-plugs">
            {BRIGHTSPARK_PLUGS.map((p, i) => (
              <li key={i} className="flex items-start gap-2 k-body text-[var(--ink)]">
                <CheckCircle2 size={16} className="text-sev-low mt-1 shrink-0" />
                <span>{p}</span>
              </li>
            ))}
          </ul>

          <p className="k-body-l text-[var(--ink)] mt-6">
            <strong>Leak Score: 31 &rarr; 67 in 90 days.</strong> Owner working ~45 hours/week (was 60+).
          </p>

          <blockquote className="mt-7 pl-5 border-l-4 border-[var(--ink)] k-body-l text-[var(--ink)] italic" data-testid="brightspark-quote">
            &ldquo;I went from drowning to clear-headed in three months. Wish I&rsquo;d done this two years ago.&rdquo;
            <footer className="not-italic mt-2 k-small text-[var(--muted)]">&mdash; Sample testimonial structure, replace with real once beta delivers</footer>
          </blockquote>
        </div>

        <aside className="md:col-span-5 min-w-0">
          <Card className="p-5 overflow-hidden" data-testid="brightspark-bucket-card">
            <p className="k-eyebrow text-[var(--muted)] mb-4 text-center">Bucket &middot; Day 1 &rarr; Day 90</p>
            <div className="grid grid-cols-2 gap-3 items-end">
              <div className="text-center min-w-0">
                <div className="w-full max-w-[160px] mx-auto" data-testid="brightspark-bucket-before"><LeakyBucket scores={BRIGHTSPARK_SCORES} fluid /></div>
                <p className="font-display font-bold text-[28px] text-[var(--ink)] mt-2 leading-none">31</p>
                <p className="k-small text-[var(--muted)] mt-1">before</p>
              </div>
              <div className="text-center min-w-0">
                <div className="w-full max-w-[160px] mx-auto" data-testid="brightspark-bucket-after"><LeakyBucket scores={BRIGHTSPARK_AFTER} fluid /></div>
                <p className="font-display font-bold text-[28px] k-spectrum-text mt-2 leading-none">67</p>
                <p className="k-small text-[var(--muted)] mt-1">after 90 days</p>
              </div>
            </div>
          </Card>
        </aside>
      </div>
    </SectionBand>

    {/* 4 illustrative archetype cards — no proper-noun names, no fictional quotes.
        See /app/memory/PRD.md for the locked code-of-conduct rule. */}
    {ARCHETYPES.map((a) => <CaseStudyCard key={a.slug} archetype={a} />)}

    {/* Honest waiting-room — verbatim copy, kept */}
    <SectionBand tone="white">
      <div className="max-w-3xl text-center mx-auto">
        <p className="k-eyebrow text-[var(--muted)] mb-3">Beta &middot; in progress</p>
        <h2 className="k-h2 text-[var(--ink)]">Real client case studies <span className="k-spectrum-text">land here next.</span></h2>
        <p className="k-body-l text-[var(--ink)] mt-4">
          We&rsquo;re running paid Blueprints now and beta case studies publish as they complete. Want yours to be the next one? Book a Blueprint and let&rsquo;s see what&rsquo;s leaking.
        </p>
        <Button as={Link} to="/book" variant="primary" size="lg" className="mt-7" data-testid="results-waiting-room-cta">Book a free 30-min call</Button>
      </div>
    </SectionBand>

    {/* CTA close — results. surface="cta" matches footer for continuous seam. */}
    <section className="relative bg-navy text-icy overflow-hidden">
      <MotifBackground surface="cta" />
      <div className="k-container relative py-16 text-center max-w-2xl mx-auto">
        <h2 className="k-h2 text-icy">Want to <span className="k-spectrum-text">score your bucket?</span></h2>
        <p className="k-body-l text-icy-muted mt-4">Same diagnostic, same Report. Two weeks, $1,950, refunded if we don&rsquo;t find $1,950.</p>
        <Button as={Link} to="/book" variant="primary" size="lg" className="mt-7" data-testid="results-final-cta">Book a free 30-min call</Button>
      </div>
    </section>
  </>
);

// ============================================================================
// /contact — simple contact form posting to CRM
// ============================================================================
export const ContactPage = () => {
  const [f, setF] = React.useState({ name: '', email: '', message: '' });
  const [done, setDone] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');
  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.email || !f.message || busy) return;
    setBusy(true); setErr('');
    try {
      const r = await axios.post(`${API}/leads/contact`, f);
      if (r.data?.ok) setDone(true);
    } catch { setErr("That didn't send — try emailing hello@kindraai.dev directly."); }
    setBusy(false);
  };
  return (
    <>
      <section className="relative bg-navy text-icy overflow-hidden pt-28 pb-14">
        <MotifBackground surface="hero" />
        <div className="k-container relative max-w-3xl text-center">
          <p className="k-eyebrow text-icy-muted mb-3">Contact</p>
          <h1 className="k-h1 text-icy">Real human. Real phone. <span className="k-spectrum-text">Real quick.</span></h1>
        </div>
      </section>
      <SectionBand tone="white">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-7">
            {done ? (
              <Card className="p-8 text-center" data-testid="contact-success">
                <CheckCircle2 size={28} className="text-sev-low mx-auto" />
                <h2 className="k-h2 text-[var(--ink)] mt-3">Got it.</h2>
                <p className="k-body text-[var(--muted)] mt-2">We'll come back to you today.</p>
              </Card>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
                <Input label="Your name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required />
                <Input label="Email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required />
                <Textarea label="What's on your mind?" rows={6} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} required />
                {err && <p className="k-small text-sev-high">{err}</p>}
                <Button type="submit" variant="primary" size="lg" disabled={busy}>{busy ? 'Sending…' : 'Send'}</Button>
              </form>
            )}
          </div>
          <aside className="md:col-span-5">
            <Card className="p-6">
              <h2 className="k-h3 text-[var(--ink)] mb-4">Or skip the form.</h2>
              <ul className="space-y-3">
                <li className="flex items-start gap-3"><Mail size={18} className="mt-0.5 text-[var(--muted)]" /><a className="k-body text-[var(--ink)] hover:opacity-80" href="mailto:hello@kindraai.dev">hello@kindraai.dev</a></li>
                <li className="flex items-start gap-3"><MapPin size={18} className="mt-0.5 text-[var(--muted)]" /><span className="k-body text-[var(--muted)]">Brisbane · Working with SMBs Australia-wide</span></li>
              </ul>
              <hr className="my-5 border-[var(--line)]" />
              <p className="k-small text-[var(--muted)]">Want a call instead? <Link to="/book" className="text-[var(--ink)] font-semibold underline-offset-4 hover:underline">Book the 30-min Leak Check call</Link>.</p>
            </Card>
          </aside>
        </div>
      </SectionBand>
    </>
  );
};

// ============================================================================
// /guide — free guide lead magnet (unchanged)
// ============================================================================
export const GuidePage = () => {
  const [f, setF] = React.useState({ first_name: '', email: '' });
  const [done, setDone] = React.useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/leads/guide`, f);
      setDone(true);
      setTimeout(() => { window.location.href = '/thank-you/guide'; }, 600);
    } catch { setDone(true); }
  };
  return (
    <>
      <section className="relative bg-navy text-icy overflow-hidden pt-28 pb-14">
        <MotifBackground surface="hero" />
        <div className="k-container relative max-w-3xl">
          <p className="k-eyebrow text-icy-muted mb-3">Free Guide</p>
          <h1 className="k-h1 text-icy">The 5 leaks <span className="k-spectrum-text">draining most small businesses</span> — and how to spot them in 10 minutes.</h1>
          <p className="k-body-l text-icy-muted mt-4 max-w-prose">A short PDF (under 10 pages). Plain English. No newsletter spam — just the guide.</p>
          <p className="k-small text-icy-muted mt-3 max-w-prose">We're putting the finishing touches on the guide PDF — we'll send it to your inbox the moment it's ready (usually within 48 hours). Pop your details in below and you're in the queue.</p>
        </div>
      </section>
      <SectionBand tone="white">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <Card className="p-6 md:p-8">
            {done ? (
              <div className="text-center" data-testid="guide-success">
                <CheckCircle2 size={28} className="text-sev-low mx-auto" />
                <h2 className="k-h3 text-[var(--ink)] mt-3">You're on the list.</h2>
                <p className="k-small text-[var(--muted)] mt-2">We'll send the guide PDF the moment it's ready — usually within 48 hours.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="guide-form">
                <Input label="First name" value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} required />
                <Input label="Email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required />
                <Button type="submit" variant="primary" size="lg" fullWidth><Download size={16} /> Reserve my copy</Button>
                <p className="k-small text-[var(--muted)] text-center">No spam. We'll send it as soon as it's ready.</p>
              </form>
            )}
          </Card>
          <div>
            <h2 className="k-h2 text-[var(--ink)]">What's inside</h2>
            <ul className="mt-4 space-y-2 k-body text-[var(--ink)]">
              {[
                'The 5 leaks named, defined, costed',
                'The 7 admin jobs most owners can stop doing this week',
                'A one-page checklist you can stick on the wall',
                'Two real Aussie SMB examples (Brightspark + T Touch)',
                'How to do a 10-minute self-Leak-Check at home',
              ].map((t, i) => <li key={i} className="flex items-start gap-2"><CheckCircle2 size={16} className="text-sev-low mt-1 shrink-0" />{t}</li>)}
            </ul>
          </div>
        </div>
      </SectionBand>
    </>
  );
};

// ============================================================================
// /thank-you/guide
// ============================================================================
export const ThankYouGuidePage = () => (
  <section className="relative bg-navy text-icy overflow-hidden min-h-[80vh] pt-28 pb-20">
    <MotifBackground surface="hero" />
    <div className="k-container relative max-w-2xl text-center">
      <div className="inline-flex h-12 w-12 rounded-pill bg-spectrum text-icy items-center justify-center mb-4"><Download size={22} /></div>
      <p className="k-eyebrow text-icy-muted mb-2">Check your inbox</p>
      <h1 className="k-h1 text-icy">The guide is on its way.</h1>
      <p className="k-body-l text-icy-muted mt-4">If it hasn't arrived in 2 minutes, peek in your spam (and add hello@kindraai.dev to safe senders).</p>
      <div className="mt-8 flex justify-center gap-3 flex-wrap">
        <Button as={Link} to="/book" variant="primary" size="lg">Book a free 30-min call</Button>
        <Button as={Link} to="/admin-tax-calculator" variant="secondary" size="lg">Try the calculator <ArrowRight size={16} /></Button>
      </div>
    </div>
  </section>
);

// ============================================================================
// /404
// ============================================================================
export const NotFoundPage = () => (
  <section className="relative bg-navy text-icy overflow-hidden min-h-[80vh] pt-28 pb-20">
    <MotifBackground surface="hero" />
    <div className="k-container relative max-w-2xl text-center">
      <p className="k-eyebrow text-icy-muted mb-2">404</p>
      <h1 className="k-h1 text-icy"><span className="k-spectrum-text">That page</span> isn't here.</h1>
      <p className="k-body-l text-icy-muted mt-4">Plugged. Patched. Or never built. Either way — let's send you somewhere useful.</p>
      <div className="mt-8 flex justify-center gap-3 flex-wrap">
        <Button as={Link} to="/" variant="primary" size="lg">Home</Button>
        <Button as={Link} to="/leak-check" variant="secondary" size="lg">The Blueprint</Button>
      </div>
    </div>
  </section>
);

// ============================================================================
// /privacy /terms — plain English legal stubs (unchanged)
// ============================================================================
const PROSE_BASE = "max-w-prose k-body text-[var(--muted)] space-y-4";

export const PrivacyPage = () => (
  <>
    <section className="relative bg-navy text-icy overflow-hidden pt-28 pb-10">
      <MotifBackground surface="hero" />
      <div className="k-container relative max-w-3xl">
        <p className="k-eyebrow text-icy-muted mb-3">Privacy</p>
        <h1 className="k-h1 text-icy">Plain English. <span className="k-spectrum-text">Your data stays yours.</span></h1>
      </div>
    </section>
    <SectionBand tone="white">
      <div className={PROSE_BASE}>
        <p><strong className="text-[var(--ink)]">Who we are.</strong> Kindra AI Pty Ltd, an Australian-owned business clarity & operations partner. Registered office: Brendale, QLD, Australia. Contact: hello@kindraai.dev.</p>
        <p><strong className="text-[var(--ink)]">What we collect.</strong> Only what you give us &mdash; your name, email, business details, intake answers, calculator inputs, and call notes. We don&rsquo;t buy lists. We don&rsquo;t track you across the web.</p>
        <p><strong className="text-[var(--ink)]">Why we collect it.</strong> To run the Blueprint, send the takeaway you asked for, schedule the call, take payment, and respond when you ask us a question. Nothing else.</p>
        <p><strong className="text-[var(--ink)]">Where it sits.</strong> HubSpot (CRM), Calendly (scheduling), Stripe (payments), Resend (email), and our app database (MongoDB hosted in Germany on Hetzner Cloud). All standard, audited providers.</p>
        <p><strong className="text-[var(--ink)]">Cookies.</strong> We use a single first-party cookie to keep you signed in if you ever log in to a client portal. We do not use third-party analytics or advertising cookies on this site.</p>
        <p><strong className="text-[var(--ink)]">Who we share it with.</strong> Nobody, unless you ask us to. We never sell your data.</p>
        <p><strong className="text-[var(--ink)]">How long we keep it.</strong> As long as we work together + 7 years to meet Australian record-keeping rules. Then it&rsquo;s deleted.</p>
        <p><strong className="text-[var(--ink)]">Your rights.</strong> Ask us anytime &mdash; we&rsquo;ll show you what we have, change it, or delete it. Email hello@kindraai.dev.</p>
        <p className="k-small mt-8">Last updated: February 2026.</p>
      </div>
    </SectionBand>
  </>
);

export const TermsPage = () => (
  <>
    <section className="relative bg-navy text-icy overflow-hidden pt-28 pb-10">
      <MotifBackground surface="hero" />
      <div className="k-container relative max-w-3xl">
        <p className="k-eyebrow text-icy-muted mb-3">Terms</p>
        <h1 className="k-h1 text-icy">Plain English. <span className="k-spectrum-text">No surprises.</span></h1>
      </div>
    </section>
    <SectionBand tone="white">
      <div className={PROSE_BASE}>
        <p><strong className="text-[var(--ink)]">Who we are.</strong> These terms are between you and Kindra AI Pty Ltd (Brendale, QLD, Australia).</p>
        <p><strong className="text-[var(--ink)]">The Blueprint.</strong> Fixed price $1,950 AUD, paid up front. Delivered in 2 weeks. You keep the Scorecard, Report and Action Plan whether or not you go ahead with any fixes.</p>
        <p><strong className="text-[var(--ink)]">Guarantee.</strong> If the Report doesn&rsquo;t surface three fixes worth more than $1,950 in conservative annual value, we refund the full fee. No haggling.</p>
        <p><strong className="text-[var(--ink)]">Implementation.</strong> Fixed-price, quoted up front. Paid 50/50 &mdash; deposit + completion. Scope changes are quoted in writing before we start them.</p>
        <p><strong className="text-[var(--ink)]">Monthly partnership.</strong> Rolling. Cancel anytime with 30 days&rsquo; notice in writing. No lock-in, no auto-renewals beyond the month.</p>
        <p><strong className="text-[var(--ink)]">Confidentiality.</strong> We don&rsquo;t share your numbers, your name, or anything we see in your business &mdash; ever.</p>
        <p><strong className="text-[var(--ink)]">Limitation.</strong> We provide advice and execution. We don&rsquo;t guarantee specific business outcomes (we do guarantee finding the leaks, see above). We&rsquo;re not your accountant or lawyer &mdash; get one for those calls.</p>
        <p><strong className="text-[var(--ink)]">Governing law.</strong> Queensland, Australia.</p>
        <p className="k-small mt-8">Last updated: February 2026.</p>
      </div>
    </SectionBand>
  </>
);
